import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import { BrandAppearanceFields, type BrandAppearanceValue } from "@/components/shell/BrandAppearanceFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import {
  useOptionalAccountWorkspace,
  type WorkspaceBrand,
} from "@/lib/account-workspace";
import { useShellRouteInfo } from "@/lib/shell-navigation";

const brandSchema = z.object({
  name: z.string().trim().min(1, "请输入品牌名称").max(100, "品牌名称不能超过 100 个字符"),
});

type BrandFormValues = z.infer<typeof brandSchema>;

type CreateBrandDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreateBrandDialog({ open, onOpenChange }: CreateBrandDialogProps) {
  const navigate = useNavigate();
  const workspace = useOptionalAccountWorkspace();
  const shell = useShellRouteInfo();
  const accountId = shell.accountId || workspace?.accountId || "";
  const [appearance, setAppearance] = useState<BrandAppearanceValue>({
    iconKey: null,
    color: null,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    defaultValues: { name: "" },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (open) {
      reset({ name: "" });
      setAppearance({ iconKey: null, color: null });
    }
  }, [open, reset]);

  async function onSubmit({ name }: BrandFormValues) {
    if (!accountId || !workspace) return;
    try {
      const { brand } = await api.fetchForAccount<{ brand: WorkspaceBrand }>(
        accountId,
        `/accounts/${accountId}/brands`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            icon_key: appearance.iconKey,
            color: appearance.color,
          }),
        },
      );
      await workspace.refreshBrands();
      onOpenChange(false);
      navigate(`/accounts/${accountId}/brands/${brand.id}`);
    } catch (caughtError) {
      setError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "创建品牌失败，请稍后重试",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>创建品牌</DialogTitle>
          <DialogDescription>创建后会出现在侧栏与企业页品牌列表中。</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-2">
            <Label htmlFor="create-brand-name">品牌名称</Label>
            <Input
              id="create-brand-name"
              autoFocus
              {...register("name")}
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.name.message}
              </p>
            ) : null}
          </div>
          <BrandAppearanceFields
            name={nameValue}
            value={appearance}
            onChange={setAppearance}
            modal
            idPrefix="create-brand"
          />
          {errors.root ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
            >
              {errors.root.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={isSubmitting || !accountId}>
              {isSubmitting ? "创建中…" : "创建"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
